// Stage B — Bestellingen CRM sectie.
// Top-level sidebar entry. Toont een lijst webshop-bestellingen met
// operationele en conversie-acties (pakbon, label, factuur, waardeverklaring,
// boekingsformulier) op het detailscherm.
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../ui/dialog";
import { toast } from "sonner";
import BestellingDetail from "./BestellingDetail";
import { Package, RefreshCw, Eye, CheckCircle2 } from "lucide-react";

const ORDER_STATUS_LABELS = {
  pending: "Openstaand", paid: "Betaald", fulfilled: "Uitgevoerd",
  cancelled: "Geannuleerd", failed: "Mislukt", refunded: "Terugbetaald",
};
const ORDER_STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800",
  paid: "bg-green-100 text-green-700",
  fulfilled: "bg-blue-100 text-blue-700",
  cancelled: "bg-gray-100 text-gray-700",
  failed: "bg-red-100 text-red-700",
  refunded: "bg-purple-100 text-purple-700",
};

const PAYMENT_LABELS = {
  paid: "Betaald", unpaid: "Onbetaald", pending: "In afwachting",
  refunded: "Terugbetaald", failed: "Mislukt",
};

const OPERATIONAL_LABELS = {
  klaargezet: "Klaargezet",
  uitgevoerd: "Uitgevoerd",
};

function StatusPill({ status }) {
  return (
    <span
      data-testid={`order-status-pill-${status}`}
      className={`inline-block text-[10px] px-2 py-0.5 font-bold uppercase tracking-wide ${ORDER_STATUS_COLORS[status] || "bg-gray-100 text-gray-700"}`}
    >
      {ORDER_STATUS_LABELS[status] || status}
    </span>
  );
}

function OperationalBadge({ order }) {
  if (order.executed_at) {
    return (
      <span className="inline-block text-[10px] px-2 py-0.5 font-bold uppercase tracking-wide bg-blue-100 text-blue-700" data-testid="op-badge-executed">
        Uitgevoerd
      </span>
    );
  }
  if (order.prepared_at) {
    return (
      <span className="inline-block text-[10px] px-2 py-0.5 font-bold uppercase tracking-wide bg-emerald-100 text-emerald-800" data-testid="op-badge-prepared">
        Klaargezet
      </span>
    );
  }
  return null;
}

export default function BestellingenSection({ initialOrderId } = {}) {
  const { hasPermission } = useAuth();
  const canView = hasPermission("webshop:orders_view");

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({
    q: "", status: "", payment: "", operational: "",
  });
  const [selectedId, setSelectedId] = useState(initialOrderId || null);

  const load = useCallback(async () => {
    if (!canView) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = { archived: false };
      if (filter.status) params.status = filter.status;
      if (filter.q) params.q = filter.q;
      const r = await adminApi.get("/admin/webshop/orders", { params });
      let rows = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      // client-side filters die de bestaande lijst-endpoint niet biedt.
      // P0 hotfix — gebruik "effectief betaald" (status='paid' OR
      // payment_status='paid') voor zowel het betaling-filter als het
      // operationele "betaald · nog niet klaargezet" filter.
      const isPaid = (o) => o.payment_status === "paid" || o.status === "paid";
      if (filter.payment === "paid") {
        rows = rows.filter(isPaid);
      } else if (filter.payment) {
        rows = rows.filter((o) => (o.payment_status || "") === filter.payment && !isPaid(o));
      }
      if (filter.operational === "open") {
        rows = rows.filter((o) => isPaid(o) && !o.prepared_at && !o.executed_at);
      } else if (filter.operational === "prepared") {
        rows = rows.filter((o) => o.prepared_at && !o.executed_at);
      } else if (filter.operational === "executed") {
        rows = rows.filter((o) => o.executed_at);
      }
      setOrders(rows);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filter, canView]);

  useEffect(() => { load(); }, [load]);

  // Bij wijziging van initialOrderId-prop (deeplink uit AdminPage) direct openen.
  // AdminPage is single source of truth voor deeplink — geen extra URL-parsing
  // hier nodig, anders kan de Terug-knop niet meer functioneren.
  useEffect(() => {
    if (initialOrderId) setSelectedId(initialOrderId);
  }, [initialOrderId]);

  const handleBackToList = useCallback(() => {
    // Verwijder order_id (en eventueel verouderde section) uit de URL zodat een
    // refresh van de pagina niet opnieuw in BestellingDetail belandt.
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has("order_id")) {
        url.searchParams.delete("order_id");
        window.history.replaceState({}, "", url.toString());
      }
    } catch (_) { /* noop */ }
    setSelectedId(null);
    load();
  }, [load]);

  const counts = useMemo(() => {
    // P0 hotfix — sommige (legacy) orders hebben status='paid' maar
    // payment_status='unpaid' omdat de webhook bij die orders nooit is gerund
    // of payment_status nooit is gesynchroniseerd. We tellen daarom een order
    // als betaald wanneer ÓF status='paid' ÓF payment_status='paid'.
    const isPaid = (o) => o.payment_status === "paid" || o.status === "paid";
    const paid = orders.filter(isPaid).length;
    const prepared = orders.filter((o) => o.prepared_at && !o.executed_at).length;
    const executed = orders.filter((o) => o.executed_at).length;
    return { total: orders.length, paid, prepared, executed };
  }, [orders]);

  if (!canView) {
    return (
      <div className="bg-white border border-gray-200 p-10 text-center" data-testid="bestellingen-no-access">
        <h2 className="font-chivo font-black text-xl text-[#082c50]">Geen toegang</h2>
        <p className="text-sm text-gray-600 mt-2">
          Je hebt geen rechten om bestellingen te bekijken.
        </p>
      </div>
    );
  }

  if (selectedId) {
    return (
      <BestellingDetail
        orderId={selectedId}
        onBack={handleBackToList}
      />
    );
  }

  return (
    <div data-testid="bestellingen-section">
      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="font-chivo font-black text-3xl text-[#0A192F] tracking-tight flex items-center gap-3">
            <Package className="w-7 h-7 text-[#0055FF]" />
            Bestellingen
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Operationele weergave van alle webshop-bestellingen. Markeer
            bestellingen als klaargezet of uitgevoerd, en zet ze om naar
            pakbon, label, factuur of waardeverklaring.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={load}
          className="rounded-none text-xs"
          data-testid="bestellingen-refresh"
        >
          <RefreshCw className="w-4 h-4 mr-1" /> Vernieuwen
        </Button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="Totaal in lijst" value={counts.total} testid="stat-total" />
        <StatCard label="Betaald" value={counts.paid} testid="stat-paid" tone="green" />
        <StatCard label="Klaargezet" value={counts.prepared} testid="stat-prepared" tone="emerald" />
        <StatCard label="Uitgevoerd" value={counts.executed} testid="stat-executed" tone="blue" />
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 p-3 flex flex-wrap gap-3 mb-4">
        <Input
          placeholder="Zoek op e-mail, naam of order-id…"
          value={filter.q}
          onChange={(e) => setFilter({ ...filter, q: e.target.value })}
          className="rounded-none max-w-xs"
          data-testid="bestellingen-filter-q"
        />
        <Select value={filter.status || "__all"} onValueChange={(v) => setFilter({ ...filter, status: v === "__all" ? "" : v })}>
          <SelectTrigger className="rounded-none w-48" data-testid="bestellingen-filter-status">
            <SelectValue placeholder="Alle statussen" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">Alle statussen</SelectItem>
            {Object.entries(ORDER_STATUS_LABELS).map(([k, l]) => (
              <SelectItem key={k} value={k}>{l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filter.payment || "__all"} onValueChange={(v) => setFilter({ ...filter, payment: v === "__all" ? "" : v })}>
          <SelectTrigger className="rounded-none w-44" data-testid="bestellingen-filter-payment">
            <SelectValue placeholder="Betaalstatus" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">Alle betaalstatussen</SelectItem>
            {Object.entries(PAYMENT_LABELS).map(([k, l]) => (
              <SelectItem key={k} value={k}>{l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filter.operational || "__all"} onValueChange={(v) => setFilter({ ...filter, operational: v === "__all" ? "" : v })}>
          <SelectTrigger className="rounded-none w-52" data-testid="bestellingen-filter-operational">
            <SelectValue placeholder="Operationeel" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">Alle (operationeel)</SelectItem>
            <SelectItem value="open">Betaald · nog niet klaargezet</SelectItem>
            <SelectItem value="prepared">Klaargezet</SelectItem>
            <SelectItem value="executed">Uitgevoerd</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tabel */}
      {loading ? (
        <div className="p-6 text-gray-500">Bestellingen laden…</div>
      ) : (
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-gray-500 border-b">
                <th className="py-3 px-4 font-bold">Datum</th>
                <th className="py-3 px-4 font-bold">Order</th>
                <th className="py-3 px-4 font-bold">Klant</th>
                <th className="py-3 px-4 font-bold">Totaal</th>
                <th className="py-3 px-4 font-bold">Status</th>
                <th className="py-3 px-4 font-bold">Operationeel</th>
                <th className="py-3 px-4 font-bold">Documenten</th>
                <th className="py-3 px-4 font-bold">Actie</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {orders.map((o) => (
                <tr key={o.id} data-testid={`bestelling-row-${o.id}`}>
                  <td className="py-3 px-4 font-mono text-xs">
                    {o.created_at?.slice(0, 16).replace("T", " ")}
                  </td>
                  <td className="py-3 px-4 font-mono text-xs">
                    {o.order_number || o.id?.slice(0, 8)}
                  </td>
                  <td className="py-3 px-4">
                    <div className="font-medium">{o.customer?.name}</div>
                    <div className="text-xs text-gray-500">{o.customer?.email}</div>
                  </td>
                  <td className="py-3 px-4 font-bold">
                    € {Number(o.amount_total || 0).toFixed(2)}
                  </td>
                  <td className="py-3 px-4">
                    <StatusPill status={o.status} />
                    {/* P0 hotfix — toon "Betaling: …" alleen wanneer order
                        nog NIET als betaald geldt. Bij status='paid' is hij
                        effectief betaald — toon dan geen tegenstrijdige
                        "Onbetaald" subtitel. */}
                    {o.payment_status &&
                      o.payment_status !== o.status &&
                      !(o.status === "paid" || o.payment_status === "paid") && (
                      <div className="text-[10px] text-gray-500 mt-1">
                        Betaling: {PAYMENT_LABELS[o.payment_status] || o.payment_status}
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <OperationalBadge order={o} />
                  </td>
                  <td className="py-3 px-4 text-xs">
                    {(o.linked_documents?.length || 0)} gekoppeld
                  </td>
                  <td className="py-3 px-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSelectedId(o.id)}
                      className="rounded-none text-xs"
                      data-testid={`bestelling-open-${o.id}`}
                    >
                      <Eye className="w-3 h-3 mr-1" /> Open
                    </Button>
                  </td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-10 text-center text-gray-500">
                    <Package className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    Geen bestellingen gevonden in deze weergave.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, testid, tone = "default" }) {
  const tones = {
    default: "bg-white border-gray-200 text-[#0A192F]",
    green: "bg-green-50 border-green-200 text-green-800",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
  };
  return (
    <div className={`border p-4 ${tones[tone]}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-70">
        {label}
      </div>
      <div className="font-chivo font-black text-3xl mt-1">{value}</div>
    </div>
  );
}
